import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactElement } from 'react';
import { cloneRig, layerTracks, uniqueName, type KeyInterp, type RigClip, type RigLayer, type RigTrack, type TangentMode } from '../../lib/rig-doc.js';
import {
  CHANNELS,
  copyKeys,
  curveKeys,
  curvePolyline,
  deleteKeys,
  formatTime,
  gridStep,
  handles,
  keyId,
  moveHandle,
  moveKeys,
  pasteKeys,
  setInterpolation,
  setKeyValue,
  setTangentMode,
  snapTime,
  type Channel,
  type KeyRef,
} from '../../lib/rig/curves.js';
import { useCommands } from '../../lib/shortcuts/index.js';
import { clipDuration, useRig } from '../../state/rig.js';
import { ContextMenu, IconButton, MenuButton, NumberField, SelectField, TextField, Toggle, type MenuItem } from '../ui/controls.js';
import './anim-editors.css';

const CHANNEL_COLORS: Record<string, string> = { x: '#ff6b6b', y: '#7ddc6f', z: '#5aa7ff' };
const INTERPOLATIONS: { id: KeyInterp; label: string }[] = [
  { id: 'constant', label: 'Constant' },
  { id: 'linear', label: 'Linear' },
  { id: 'bezier', label: 'Bezier' },
];
const TANGENTS: { id: TangentMode; label: string }[] = [
  { id: 'auto', label: 'Auto (clamped)' },
  { id: 'aligned', label: 'Aligned' },
  { id: 'free', label: 'Free' },
  { id: 'linear', label: 'Linear' },
  { id: 'constant', label: 'Constant' },
];

let clipboard: ReturnType<typeof copyKeys> = [];

function activeClip(): RigClip | undefined {
  const s = useRig.getState();
  return s.rig.clips?.find((c) => c.name === s.clip);
}
function activeTracks(): RigTrack[] {
  const c = activeClip();
  return (c && layerTracks(c, useRig.getState().layer)) ?? [];
}
function commitRig(rig: ReturnType<typeof cloneRig>) {
  const s = useRig.getState();
  s.commit(rig);
}
/** Every key of the active layer (select all). */
function allKeys(): KeyRef[] {
  return activeTracks().flatMap((t) => t.keys.map((k) => ({ joint: t.joint, time: k.time })));
}

export function interpolationMenu(): MenuItem[] {
  const s = useRig.getState();
  const keys = s.keySelection;
  return [
    { id: 'h-i', label: 'Interpolation', heading: true },
    ...INTERPOLATIONS.map((i) => ({
      id: `i-${i.id}`,
      label: i.label,
      disabled: !keys.length,
      run: () => commitRig(setInterpolation(s.rig, s.clip, s.layer, keys, i.id)),
    })),
  ];
}
export function tangentMenu(channels?: readonly Channel[]): MenuItem[] {
  const s = useRig.getState();
  const keys = s.keySelection;
  return [
    { id: 'h-t', label: 'Handle type', heading: true },
    ...TANGENTS.map((t) => ({
      id: `t-${t.id}`,
      label: t.label,
      disabled: !keys.length,
      run: () => commitRig(setTangentMode(s.rig, s.clip, s.layer, keys, t.id, channels)),
    })),
  ];
}

/** Shortcut commands shared by the dope sheet and graph editor. */
function useKeyCommands(setMenu: (m: { x: number; y: number; items: MenuItem[] } | null) => void) {
  const animating = () => useRig.getState().mode === 'animate' && Boolean(useRig.getState().clip);
  const center = () => ({ x: window.innerWidth / 2 - 80, y: window.innerHeight / 2 - 60 });
  useCommands(
    [
        {
          id: 'rig.keys.selectAll',
          label: 'Select all keys',
          category: 'Rig & Animate',
          context: 'model.rig',
          keys: ['A'],
          enabled: animating,
          run: () => {
            const s = useRig.getState();
            useRig.setState({ keySelection: s.keySelection.length ? [] : allKeys() });
          },
        },
        {
          id: 'rig.keys.delete',
          label: 'Delete selected keys',
          category: 'Rig & Animate',
          context: 'model.rig',
          keys: ['Delete'],
          enabled: () => animating() && useRig.getState().keySelection.length > 0,
          run: () => {
            const s = useRig.getState();
            commitRig(deleteKeys(s.rig, s.clip, s.layer, s.keySelection));
            useRig.setState({ keySelection: [] });
          },
        },
        {
          id: 'rig.keys.copy',
          label: 'Copy keys',
          category: 'Rig & Animate',
          context: 'model.rig',
          keys: ['Ctrl+C'],
          enabled: () => animating() && useRig.getState().keySelection.length > 0,
          run: () => {
            const s = useRig.getState();
            clipboard = copyKeys(s.rig, s.clip, s.layer, s.keySelection);
          },
        },
        {
          id: 'rig.keys.paste',
          label: 'Paste keys at the playhead',
          category: 'Rig & Animate',
          context: 'model.rig',
          keys: ['Ctrl+V'],
          enabled: () => animating() && clipboard.length > 0,
          run: () => {
            const s = useRig.getState();
            commitRig(pasteKeys(s.rig, s.clip, s.layer, clipboard, s.time));
          },
        },
        {
          id: 'rig.keys.interpolation',
          label: 'Key interpolation…',
          category: 'Rig & Animate',
          context: 'model.rig',
          keys: ['T'],
          enabled: () => animating() && useRig.getState().keySelection.length > 0,
          run: () => setMenu({ ...center(), items: interpolationMenu() }),
        },
        {
          id: 'rig.keys.handles',
          label: 'Key handle type…',
          category: 'Rig & Animate',
          context: 'model.rig',
          keys: ['V'],
          enabled: () => animating() && useRig.getState().keySelection.length > 0,
          run: () => setMenu({ ...center(), items: tangentMenu() }),
        },
        {
          id: 'rig.keys.modified',
          label: 'Key all modified joints',
          category: 'Rig & Animate',
          context: 'model.rig',
          keys: ['Shift+K'],
          enabled: () => animating() && Object.keys(useRig.getState().modified).length > 0,
          run: () => useRig.getState().keyModified(),
        },
        {
          id: 'rig.record',
          label: 'Toggle record (auto key)',
          category: 'Rig & Animate',
          context: 'model.rig',
          keys: ['Shift+R'],
          enabled: animating,
          run: () => useRig.setState({ autoKey: !useRig.getState().autoKey }),
        },
        {
          id: 'rig.view.toggle',
          label: 'Toggle dope sheet / graph editor',
          category: 'Rig & Animate',
          context: 'model.rig',
          keys: [],
          enabled: animating,
          run: () => useRig.setState({ animView: useRig.getState().animView === 'graph' ? 'dopesheet' : 'graph' }),
        },
    ],
    [],
  );
}

/** Record / key-modified / onion skin / view / snap controls. */
function AnimToolbar() {
  const autoKey = useRig((s) => s.autoKey),
    modified = useRig((s) => s.modified),
    onion = useRig((s) => s.onion),
    view = useRig((s) => s.animView),
    snap = useRig((s) => s.snap),
    frames = useRig((s) => s.showFrames),
    clip = useRig((s) => s.clip);
  const count = Object.keys(modified).length;
  return (
    <div className="anim-toolbar">
      <div className="anim-tabs" role="tablist" aria-label="Animation editor">
        <button type="button" role="tab" aria-selected={view === 'dopesheet'} className={view === 'dopesheet' ? 'is-active' : ''} onClick={() => useRig.setState({ animView: 'dopesheet' })}>
          Dope Sheet
        </button>
        <button type="button" role="tab" aria-selected={view === 'graph'} className={view === 'graph' ? 'is-active' : ''} onClick={() => useRig.setState({ animView: 'graph' })}>
          Graph Editor
        </button>
      </div>
      <button
        type="button"
        className={`anim-record${autoKey ? ' is-on' : ''}`}
        aria-pressed={autoKey}
        disabled={!clip}
        title="Record: every pose edit sets a key (Shift+R)"
        onClick={() => useRig.setState({ autoKey: !autoKey })}
      >
        <span className="anim-record-dot" /> Rec
      </button>
      <button type="button" className="ghost small" disabled={!count} title="Key every joint posed since its last key (Shift+K)" onClick={() => useRig.getState().keyModified()}>
        Key modified{count ? ` (${count})` : ''}
      </button>
      <MenuButton className="ghost small" title="Key interpolation (T)" items={interpolationMenu}>
        Interpolation
      </MenuButton>
      <MenuButton className="ghost small" title="Handle type (V)" items={() => tangentMenu()}>
        Handles
      </MenuButton>
      <span className="anim-sep" />
      <Toggle checked={snap} onChange={(on) => useRig.setState({ snap: on })} label="Snap" />
      <Toggle checked={frames} onChange={(on) => useRig.setState({ showFrames: on })} label="Frames" />
      <span className="anim-sep" />
      <IconButton icon="onion" title="Onion skin" active={onion.enabled} disabled={!clip} onClick={() => useRig.getState().setOnion({ enabled: !onion.enabled })} />
      {onion.enabled && (
        <span className="anim-onion">
          <label title="Ghosts before">
            −<NumberField value={onion.before} integer min={0} max={8} onChange={(v) => useRig.getState().setOnion({ before: Math.round(v) })} />
          </label>
          <label title="Ghosts after">
            +<NumberField value={onion.after} integer min={0} max={8} onChange={(v) => useRig.getState().setOnion({ after: Math.round(v) })} />
          </label>
          <label title="Frames between ghosts">
            step <NumberField value={onion.step} integer min={1} max={30} onChange={(v) => useRig.getState().setOnion({ step: Math.max(1, Math.round(v)) })} />
          </label>
        </span>
      )}
    </div>
  );
}

/** Clip layers: base + override / additive layers with weight, mask, mute. */
function LayersPanel() {
  const rig = useRig((s) => s.rig),
    clipName = useRig((s) => s.clip),
    layer = useRig((s) => s.layer);
  const clip = rig.clips?.find((c) => c.name === clipName);
  const [open, setOpen] = useState<number | null>(null);
  if (!clip) return null;
  const edit = (i: number, fn: (l: RigLayer) => void) => {
    const next = cloneRig(rig),
      l = next.clips?.find((c) => c.name === clipName)?.layers?.[i];
    if (!l) return;
    fn(l);
    useRig.getState().commit(next);
  };
  const add = (blend: 'override' | 'additive') => {
    const next = cloneRig(rig),
      c = next.clips!.find((x) => x.name === clipName)!;
    c.layers ??= [];
    c.layers.push({ name: uniqueName(blend === 'additive' ? 'Additive' : 'Layer', c.layers.map((l) => l.name)), blend, weight: 1, tracks: [] });
    useRig.getState().commit(next);
    useRig.setState({ layer: c.layers.length - 1, keySelection: [] });
  };
  return (
    <div className="anim-layers" aria-label="Animation layers">
      <button type="button" className={`anim-layer${layer < 0 ? ' is-active' : ''}`} onClick={() => useRig.setState({ layer: -1, keySelection: [] })}>
        Base
      </button>
      {(clip.layers ?? []).map((l, i) => (
        <span key={i} className={`anim-layer${layer === i ? ' is-active' : ''}${l.mute ? ' is-muted' : ''}`}>
          <button type="button" className="anim-layer-name" onClick={() => useRig.setState({ layer: i, keySelection: [] })} title={`${l.blend ?? 'override'} · weight ${l.weight ?? 1}`}>
            {l.blend === 'additive' ? '+ ' : ''}
            {l.name}
          </button>
          <IconButton icon={l.mute ? 'eyeOff' : 'eye'} size={12} title={l.mute ? 'Unmute layer' : 'Mute layer'} onClick={() => edit(i, (x) => (x.mute = !x.mute))} />
          <IconButton icon="settings" size={12} title="Layer settings" active={open === i} onClick={() => setOpen(open === i ? null : i)} />
        </span>
      ))}
      <MenuButton
        className="ghost small"
        title="Add a layer"
        items={[
          { id: 'o', label: 'Override layer', run: () => add('override') },
          { id: 'a', label: 'Additive layer', run: () => add('additive') },
        ]}
      >
        + Layer
      </MenuButton>
      {open !== null && clip.layers?.[open] && (
        <div className="anim-layer-settings">
          <label>
            Name
            <TextField
              value={clip.layers[open].name}
              onChange={(name) => {
                if (name.trim() && !clip.layers!.some((l, j) => j !== open && l.name === name)) edit(open, (x) => (x.name = name));
              }}
            />
          </label>
          <label>
            Blend
            <SelectField
              value={clip.layers[open].blend ?? 'override'}
              options={[
                { value: 'override', label: 'Override' },
                { value: 'additive', label: 'Additive' },
              ]}
              onChange={(v) => edit(open, (x) => (x.blend = v as 'override' | 'additive'))}
            />
          </label>
          <label>
            Weight
            <NumberField value={clip.layers[open].weight ?? 1} min={0} max={1} step={0.05} onChange={(v) => edit(open, (x) => (x.weight = Math.max(0, Math.min(1, v))))} />
          </label>
          <label title='Joint subtrees, comma separated; "!Name" excludes (empty = all joints)'>
            Mask
            <TextField
              value={(clip.layers[open].mask ?? []).join(', ')}
              onChange={(v) =>
                edit(open, (x) => {
                  const mask = v.split(',').map((m) => m.trim()).filter(Boolean);
                  if (mask.length) x.mask = mask;
                  else delete x.mask;
                })
              }
            />
          </label>
          <button
            type="button"
            className="ghost small danger"
            onClick={() => {
              const next = cloneRig(rig);
              next.clips!.find((c) => c.name === clipName)!.layers!.splice(open, 1);
              useRig.getState().commit(next);
              useRig.setState({ layer: -1, keySelection: [] });
              setOpen(null);
            }}
          >
            Delete layer
          </button>
        </div>
      )}
    </div>
  );
}

/** Time ruler with frame / second labels; click or drag to scrub. */
function Ruler({ end, onSeek }: { end: number; onSeek: (t: number) => void }) {
  const fps = useRig((s) => s.fps),
    frames = useRig((s) => s.showFrames),
    time = useRig((s) => s.time);
  const step = frames ? Math.max(1, gridStep(end * fps, 10)) / fps : gridStep(end, 10);
  const ticks: number[] = [];
  for (let t = 0; t <= end + 1e-6 && ticks.length < 200; t += step) ticks.push(t);
  const at = (e: ReactPointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    onSeek(Math.max(0, Math.min(end, ((e.clientX - r.left) / r.width) * end)));
  };
  return (
    <div
      className="anim-ruler"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        at(e);
      }}
      onPointerMove={(e) => {
        if (e.buttons & 1) at(e);
      }}
    >
      {ticks.map((t) => (
        <span key={t} className="anim-tick" style={{ left: `${(t / end) * 100}%` }}>
          {formatTime(t, fps, frames)}
        </span>
      ))}
      <span className="rig-playhead" style={{ left: `${(time / end) * 100}%` }} />
    </div>
  );
}

/** Dope sheet: key diamonds per joint of the active layer with multi-select and drag. */
function DopeSheet({ end, onSeek }: { end: number; onSeek: (t: number) => void }) {
  const rig = useRig((s) => s.rig),
    clipName = useRig((s) => s.clip),
    layer = useRig((s) => s.layer),
    selection = useRig((s) => s.keySelection),
    selectedJoint = useRig((s) => s.selected),
    time = useRig((s) => s.time),
    fps = useRig((s) => s.fps),
    snap = useRig((s) => s.snap);
  const clip = rig.clips?.find((c) => c.name === clipName);
  const tracks = (clip && layerTracks(clip, layer)) ?? [];
  const selected = useMemo(() => new Set(selection.map(keyId)), [selection]);
  const drag = useRef<{ x: number; width: number; dt: number } | null>(null);
  const [offset, setOffset] = useState(0);
  const times = [...new Set(tracks.flatMap((t) => t.keys.map((k) => k.time.toFixed(5))))].map(Number);
  const keyStyle = (interp?: KeyInterp) => (interp === 'constant' ? ' is-constant' : interp === 'bezier' ? ' is-bezier' : '');
  const lane = (track: RigTrack | null, keys: { time: number; joint?: string; interp?: KeyInterp }[]) => (
    <div
      className="rig-track-lane"
      onPointerDown={(e) => {
        if (e.target !== e.currentTarget) return;
        const r = e.currentTarget.getBoundingClientRect();
        if (!e.shiftKey) useRig.setState({ keySelection: [] });
        onSeek(((e.clientX - r.left) / r.width) * end);
      }}
    >
      <span className="rig-playhead" style={{ left: `${(time / end) * 100}%` }} />
      {keys.map((k) => {
        const refs: KeyRef[] = track ? [{ joint: track.joint, time: k.time }] : tracks.filter((t) => t.keys.some((x) => Math.abs(x.time - k.time) < 1e-5)).map((t) => ({ joint: t.joint, time: k.time }));
        const isSel = refs.every((r) => selected.has(keyId(r)));
        const shown = isSel && drag.current ? k.time + offset : k.time;
        return (
          <button
            key={k.time}
            type="button"
            className={`rig-key anim-key${isSel ? ' is-selected' : ''}${Math.abs(k.time - time) < 1e-5 ? ' is-active' : ''}${keyStyle(k.interp)}`}
            style={{ left: `${(shown / end) * 100}%` }}
            aria-label={`${track?.joint ?? 'All joints'} key at ${formatTime(k.time, fps, true)}`}
            title={`${formatTime(k.time, fps, true)} (${k.time.toFixed(3)} s) · ${k.interp ?? 'linear'} · drag to retime, Shift+click to add`}
            onPointerDown={(e) => {
              e.stopPropagation();
              if (e.shiftKey) {
                const s = useRig.getState();
                const ids = new Set(s.keySelection.map(keyId));
                const all = refs.every((r) => ids.has(keyId(r)));
                useRig.setState({ keySelection: all ? s.keySelection.filter((x) => !refs.some((r) => keyId(r) === keyId(x))) : [...s.keySelection, ...refs.filter((r) => !ids.has(keyId(r)))] });
                return;
              }
              if (!isSel) useRig.setState({ keySelection: refs });
              if (track) useRig.getState().select(track.joint);
              e.currentTarget.setPointerCapture(e.pointerId);
              drag.current = { x: e.clientX, width: e.currentTarget.parentElement!.getBoundingClientRect().width, dt: 0 };
              setOffset(0);
            }}
            onPointerMove={(e) => {
              const d = drag.current;
              if (!d) return;
              const raw = ((e.clientX - d.x) / d.width) * end;
              d.dt = snap ? Math.round(raw * fps) / fps : raw;
              setOffset(d.dt);
            }}
            onPointerUp={() => {
              const d = drag.current;
              drag.current = null;
              setOffset(0);
              if (!d) return;
              if (Math.abs(d.dt) < 1e-6) {
                onSeek(k.time);
                return;
              }
              const s = useRig.getState();
              const moved = moveKeys(s.rig, s.clip, s.layer, s.keySelection, d.dt, fps, snap);
              s.commit(moved.rig);
              useRig.setState({ keySelection: moved.keys });
            }}
            onPointerCancel={() => {
              drag.current = null;
              setOffset(0);
            }}
          />
        );
      })}
    </div>
  );
  return (
    <div className="rig-tracks anim-dopesheet">
      {tracks.length > 0 && (
        <div className="rig-track anim-summary">
          <span className="rig-track-name">Summary</span>
          {lane(
            null,
            times.map((t) => ({ time: t })),
          )}
        </div>
      )}
      {tracks.map((track) => (
        <div className={`rig-track${selectedJoint === track.joint ? ' is-selected' : ''}`} key={track.joint}>
          <button className="rig-track-name" onClick={() => useRig.getState().select(track.joint)}>
            {track.joint}
          </button>
          {lane(
            track,
            track.keys.map((k) => ({ time: k.time, interp: k.interpolation })),
          )}
        </div>
      ))}
      {!clip && <p className="muted">Choose or create an animation clip.</p>}
      {clip && !tracks.length && <p className="muted">{layer < 0 ? 'No keys yet: pose a joint and key it.' : 'This layer has no keys yet: pose joints with the layer active and key them.'}</p>}
    </div>
  );
}

/** Graph editor: per-channel curves of the selected joint with draggable keys and bezier handles. */
function GraphEditor({ end, onSeek }: { end: number; onSeek: (t: number) => void }) {
  const rig = useRig((s) => s.rig),
    clipName = useRig((s) => s.clip),
    layer = useRig((s) => s.layer),
    joint = useRig((s) => s.selected),
    selection = useRig((s) => s.keySelection),
    time = useRig((s) => s.time),
    fps = useRig((s) => s.fps),
    snap = useRig((s) => s.snap),
    frames = useRig((s) => s.showFrames);
  const clip = rig.clips?.find((c) => c.name === clipName);
  const track = ((clip && layerTracks(clip, layer)) ?? []).find((t) => t.joint === joint);
  const [visible, setVisible] = useState<Set<Channel>>(() => new Set(['rotation.x', 'rotation.y', 'rotation.z'] as Channel[]));
  const host = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<[number, number]>([600, 200]);
  const [view, setView] = useState<{ t0: number; t1: number; v0: number; v1: number } | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; items: MenuItem[] } | null>(null);
  const drag = useRef<
    | { kind: 'key'; ref: KeyRef; channel: Channel; x: number; y: number; time: number; value: number; preview?: { time: number; value: number } }
    | { kind: 'handle'; ref: KeyRef; channel: Channel; side: 'in' | 'out' }
    | { kind: 'pan'; x: number; y: number; view: { t0: number; t1: number; v0: number; v1: number } }
    | null
  >(null);
  const [, force] = useState(0);
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const o = new ResizeObserver(([e]) => e && setSize([e.contentRect.width, e.contentRect.height]));
    o.observe(el);
    return () => o.disconnect();
  }, []);
  const channels = CHANNELS.filter((c) => visible.has(c));
  const curves = useMemo(() => (track ? channels.map((ch) => ({ ch, keys: curveKeys(track, ch) })) : []), [track, channels.join()]);
  const fitView = () => {
    let lo = Infinity,
      hi = -Infinity;
    for (const c of curves)
      for (const [, v] of curvePolyline(c.keys, 0, end, 120)) {
        lo = Math.min(lo, v);
        hi = Math.max(hi, v);
      }
    if (!Number.isFinite(lo)) [lo, hi] = [-1, 1];
    const pad = Math.max((hi - lo) * 0.12, 0.05);
    setView({ t0: -end * 0.02, t1: end * 1.02, v0: lo - pad, v1: hi + pad });
  };
  useEffect(fitView, [joint, clipName, layer, visible]);
  const v = view ?? { t0: 0, t1: end, v0: -1, v1: 1 };
  const [w, h] = size;
  const X = (t: number) => ((t - v.t0) / (v.t1 - v.t0)) * w;
  const Y = (val: number) => h - ((val - v.v0) / (v.v1 - v.v0)) * h;
  const T = (x: number) => v.t0 + (x / w) * (v.t1 - v.t0);
  const V = (y: number) => v.v0 + ((h - y) / h) * (v.v1 - v.v0);
  const selected = new Set(selection.map(keyId));
  const tStep = frames ? Math.max(1, gridStep((v.t1 - v.t0) * fps, 10)) / fps : gridStep(v.t1 - v.t0, 10);
  const vStep = gridStep(v.v1 - v.v0, 6);
  const grid: ReactElement[] = [];
  for (let t = Math.ceil(v.t0 / tStep) * tStep; t <= v.t1 && grid.length < 200; t += tStep)
    grid.push(
      <g key={`t${t}`}>
        <line x1={X(t)} x2={X(t)} y1={0} y2={h} className="graph-grid" />
        <text x={X(t) + 2} y={11} className="graph-label">
          {formatTime(t, fps, frames)}
        </text>
      </g>,
    );
  for (let val = Math.ceil(v.v0 / vStep) * vStep; val <= v.v1 && grid.length < 400; val += vStep)
    grid.push(
      <g key={`v${val}`}>
        <line x1={0} x2={w} y1={Y(val)} y2={Y(val)} className="graph-grid" />
        <text x={2} y={Y(val) - 2} className="graph-label">
          {Math.abs(val) < 1e-9 ? 0 : +val.toPrecision(4)}
        </text>
      </g>,
    );
  const local = (e: { clientX: number; clientY: number }) => {
    const r = host.current!.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top] as const;
  };
  const onMove = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const [x, y] = local(e);
    const s = useRig.getState();
    if (d.kind === 'pan') {
      const dt = ((x - d.x) / w) * (d.view.t1 - d.view.t0),
        dv = ((y - d.y) / h) * (d.view.v1 - d.view.v0);
      setView({ t0: d.view.t0 - dt, t1: d.view.t1 - dt, v0: d.view.v0 + dv, v1: d.view.v1 + dv });
    } else if (d.kind === 'key') {
      const nt = Math.max(0, snapTime(T(x), fps, snap));
      d.preview = { time: e.altKey ? d.time : nt, value: e.shiftKey ? d.value : V(y) };
      force((n) => n + 1);
    } else if (d.kind === 'handle' && track) {
      const k = track.keys.find((kk) => Math.abs(kk.time - d.ref.time) < 1e-5);
      if (!k) return;
      const ck = curveKeys(track, d.channel).find((c) => Math.abs(c.time - d.ref.time) < 1e-5)!;
      s.commit(moveHandle(s.rig, s.clip, s.layer, d.ref, d.channel, d.side, [T(x) - ck.time, V(y) - ck.value]));
    }
  };
  const onUp = () => {
    const d = drag.current;
    drag.current = null;
    if (d?.kind === 'key' && d.preview) {
      const s = useRig.getState();
      let next = setKeyValue(s.rig, s.clip, s.layer, d.ref, d.channel, d.preview.value);
      let ref = d.ref;
      if (Math.abs(d.preview.time - d.time) > 1e-6) {
        const moved = moveKeys(next, s.clip, s.layer, [d.ref], d.preview.time - d.time, fps, snap);
        next = moved.rig;
        ref = moved.keys[0] ?? ref;
      }
      s.commit(next);
      useRig.setState({ keySelection: [ref] });
      onSeek(ref.time);
    }
    force((n) => n + 1);
  };
  return (
    <div className="anim-graph">
      <div className="anim-graph-channels">
        {(['position', 'rotation', 'scale'] as const).map((g) => (
          <div key={g} className="anim-graph-group">
            <span className="muted">{g}</span>
            {(['x', 'y', 'z'] as const).map((a) => {
              const ch = `${g}.${a}` as Channel;
              return (
                <button
                  key={ch}
                  type="button"
                  className={`anim-chan${visible.has(ch) ? ' is-on' : ''}`}
                  style={{ color: CHANNEL_COLORS[a] }}
                  aria-pressed={visible.has(ch)}
                  onClick={(e) => {
                    const next = new Set(e.shiftKey ? visible : []);
                    if (visible.has(ch) && e.shiftKey) next.delete(ch);
                    else next.add(ch);
                    setVisible(next);
                  }}
                  title="Click to show only this channel, Shift+click to toggle"
                >
                  {a.toUpperCase()}
                </button>
              );
            })}
          </div>
        ))}
        <button type="button" className="ghost small" onClick={fitView} title="Frame all (Home)">
          Frame all
        </button>
      </div>
      <div
        ref={host}
        className="anim-graph-canvas"
        onWheel={(e) => {
          const [x, y] = local(e);
          const k = e.deltaY > 0 ? 1.15 : 1 / 1.15;
          if (e.ctrlKey || e.shiftKey) {
            const val = V(y);
            setView({ ...v, v0: val + (v.v0 - val) * k, v1: val + (v.v1 - val) * k });
          } else {
            const t = T(x);
            setView({ ...v, t0: t + (v.t0 - t) * k, t1: t + (v.t1 - t) * k });
          }
        }}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={() => (drag.current = null)}
        onKeyDown={(e) => {
          if (e.key === 'Home') fitView();
        }}
        tabIndex={0}
      >
        <svg width={w} height={h} role="img" aria-label="Animation curves">
          <rect
            width={w}
            height={h}
            className="graph-bg"
            onPointerDown={(e) => {
              const [x, y] = local(e);
              (e.currentTarget.parentElement!.parentElement as HTMLElement).setPointerCapture(e.pointerId);
              if (e.button === 1 || e.altKey || e.button === 2) drag.current = { kind: 'pan', x, y, view: v };
              else {
                if (!e.shiftKey) useRig.setState({ keySelection: [] });
                onSeek(Math.max(0, Math.min(end, snapTime(T(x), fps, snap))));
                drag.current = { kind: 'pan', x, y, view: v };
              }
            }}
            onContextMenu={(e) => e.preventDefault()}
          />
          {grid}
          <line x1={X(time)} x2={X(time)} y1={0} y2={h} className="graph-playhead" />
          <line x1={X(end)} x2={X(end)} y1={0} y2={h} className="graph-end" />
          {curves.map(({ ch, keys }) => {
            const color = CHANNEL_COLORS[ch.slice(-1)]!;
            const hs = handles(keys);
            const d = drag.current;
            const shownKeys = keys.map((k) =>
              d?.kind === 'key' && d.channel === ch && Math.abs(d.ref.time - k.time) < 1e-5 && d.preview ? { ...k, time: d.preview.time, value: d.preview.value } : k,
            );
            const pts = curvePolyline(
              [...shownKeys].sort((a, b) => a.time - b.time),
              Math.max(v.t0, 0) - 0.5,
              v.t1,
              Math.max(60, Math.round(w / 3)),
            );
            return (
              <g key={ch}>
                <polyline points={pts.map(([t, val]) => `${X(t)},${Y(val)}`).join(' ')} fill="none" stroke={color} strokeWidth={1.6} />
                {shownKeys.map((k, i) => {
                  const ref = { joint: joint!, time: keys[i]!.time };
                  const isSel = selected.has(keyId(ref));
                  const [inh, out] = hs[i]!;
                  const bezier = k.interp === 'bezier' || keys[i - 1]?.interp === 'bezier';
                  return (
                    <g key={i}>
                      {isSel && bezier && (
                        <>
                          {(['in', 'out'] as const).map((side) => {
                            const hh = side === 'in' ? inh : out;
                            if ((side === 'in' && i === 0) || (side === 'out' && i === keys.length - 1)) return null;
                            const hx = X(k.time + hh[0]),
                              hy = Y(k.value + hh[1]);
                            return (
                              <g key={side}>
                                <line x1={X(k.time)} y1={Y(k.value)} x2={hx} y2={hy} className="graph-handle-line" />
                                <circle
                                  cx={hx}
                                  cy={hy}
                                  r={4}
                                  className="graph-handle"
                                  onPointerDown={(e) => {
                                    e.stopPropagation();
                                    host.current!.setPointerCapture(e.pointerId);
                                    drag.current = { kind: 'handle', ref, channel: ch, side };
                                  }}
                                />
                              </g>
                            );
                          })}
                        </>
                      )}
                      <rect
                        x={X(k.time) - 4}
                        y={Y(k.value) - 4}
                        width={8}
                        height={8}
                        transform={`rotate(45 ${X(k.time)} ${Y(k.value)})`}
                        className={`graph-key${isSel ? ' is-selected' : ''}`}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          const s = useRig.getState();
                          if (e.shiftKey) {
                            useRig.setState({ keySelection: isSel ? s.keySelection.filter((x) => keyId(x) !== keyId(ref)) : [...s.keySelection, ref] });
                            return;
                          }
                          if (!isSel) useRig.setState({ keySelection: [ref] });
                          host.current!.setPointerCapture(e.pointerId);
                          const [x, y] = local(e);
                          drag.current = { kind: 'key', ref, channel: ch, x, y, time: k.time, value: k.value };
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          if (!isSel) useRig.setState({ keySelection: [ref] });
                          setMenu({ x: e.clientX, y: e.clientY, items: [...interpolationMenu(), { id: 'sep', label: '', separator: true }, ...tangentMenu([ch])] });
                        }}
                      >
                        <title>{`${ch} = ${k.value.toFixed(3)} at ${formatTime(k.time, fps, true)} · ${k.interp} / ${k.mode}`}</title>
                      </rect>
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
        {!track && <p className="anim-graph-empty muted">{joint ? `${joint} has no keys on this layer.` : 'Select a joint to see its curves.'}</p>}
      </div>
      {menu && <ContextMenu x={menu.x} y={menu.y} items={menu.items} onClose={() => setMenu(null)} />}
    </div>
  );
}

/** Dope sheet / graph editor with layers, record, key-modified and onion skin. */
export function AnimationEditors() {
  const view = useRig((s) => s.animView),
    fps = useRig((s) => s.fps);
  useRig((s) => s.rig);
  const end = Math.max(clipDuration(), 1 / fps);
  const [menu, setMenu] = useState<{ x: number; y: number; items: MenuItem[] } | null>(null);
  useKeyCommands(setMenu);
  const seek = (t: number) => {
    useRig.setState({ playing: false });
    const s = useRig.getState();
    s.seek(snapTime(Math.max(0, t), s.fps, s.snap));
  };
  return (
    <div className="anim-editors">
      <AnimToolbar />
      <LayersPanel />
      <Ruler end={end} onSeek={seek} />
      {view === 'graph' ? <GraphEditor end={end} onSeek={seek} /> : <DopeSheet end={end} onSeek={seek} />}
      {menu && <ContextMenu x={menu.x} y={menu.y} items={menu.items} onClose={() => setMenu(null)} />}
    </div>
  );
}
